import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import '@adornis/bh-timeframes/components/bh-page-admin-einrichtung-timeframes.js';
import {
  BuchungsheldLitElement,
  institutionPageActionConfig,
} from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { subscribeAdminInstitutionViewSetting } from '@adornis/buchungsheld-db/api/AdminInstitutionViewSetting.js';
import { AdminInstitutionViewSetting } from '@adornis/buchungsheld-db/entities/AdminInstitutionViewSetting.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { RouterInstitutionPages } from '@adornis/buchungsheld-db/enums/routes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-section.js';
import { useObservable } from '@adornis/functional-lit/stateful.js';
import { webcomponent } from '@adornis/functional-lit/webcomponent.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { computed, watch } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, filter, from, of, switchMap } from 'rxjs';
import '../../components/bh-timeframe-selection.js';
import { provideContextEntity } from '../../util.js';
import './bh-page-admin-einrichtung-form-config.js';
import './bh-page-admin-einrichtung-import-export.js';
import './bh-page-admin-einrichtung-lecturer-detail';
import './bh-page-admin-einrichtung-lecturers';
import './bh-page-admin-einrichtung-statistics.js';
import './bh-page-admin-einrichtung-user-detail.js';
import './bh-page-admin-einrichtung-users.js';
import { adminEinrichtungUserRouter } from './bh-page-admin-einrichtung-users.js';
import './einrichtung/bh-page-admin-einrichtung-categories.js';
import './einrichtung/bundles/bh-page-admin-einrichtung-bundles.js';
import { adminEinrichtungBundlesRouter } from './einrichtung/bundles/bh-page-admin-einrichtung-bundles.js';
import './einrichtung/cosunseling/bh-page-admin-einrichtung-counselings.js';
import { adminEinrichtungCounselingsRouter } from './einrichtung/cosunseling/bh-page-admin-einrichtung-counselings.js';
import './einrichtung/settings/bh-page-admin-einrichtung-settings-mails.js';
import './einrichtung/settings/bh-page-admin-einrichtung-settings.js';
import { adminEinrichtungSettingsRouter } from './einrichtung/settings/bh-page-admin-einrichtung-settings.js';
import { adminEinrichtungWorkshopSetRouter } from './einrichtung/workshop-sets/admin-einrichtung-workshop-set-router.js';
import './einrichtung/workshops/bh-page-admin-einrichtung-workshops.js';
import { adminEinrichtungWorkshopsRouter } from './einrichtung/workshops/bh-page-admin-einrichtung-workshops.js';

const Overview = webcomponent(
  'bh-page-admin-einrichtung-overview',
  ({ institutionID }: { institutionID: string | undefined }) => {
    const institution = useObservable(
      () => (!institutionID ? of(null) : from(getByID(Institution, institutionID)(Institution.allFields))),
      [institutionID],
    );

    return html`
      <tr-flex space="md">
        <bh-breadcrumb .items=${[{ content: institution?.name ?? '', active: true }]} hide-up-button></bh-breadcrumb>
        <x-grid auto-fill-columns="300px" space="md" ${acss({ 'tr-card': { height: '100%' } })}>
          ${permissionGuard(
            Permission.VIEW_ADMIN_INSTITUTION_WORKSHOPS,
            () => html`
              <tr-card href="/admin/institution-details/${institutionID}/workshops">
                <x-h2> ${clientTranslator.translate('workshop', 'plural')} </x-h2>
                <x-text> ${clientTranslator.translate('workshop', 'info')} </x-text>
              </tr-card>
            `,
            { forbiddenTemplate: nothing, context: ANY_CONTEXT },
          )}
          ${institution?._feature_counselings
            ? permissionGuard(
                Permission.VIEW_ADMIN_INSTITUTION_COUNSELINGS,
                () => html`
                  <tr-card href="/admin/institution-details/${institutionID}/counselings">
                    <x-h2> ${clientTranslator.translate('counseling', 'plural')} </x-h2>
                    <x-text> ${clientTranslator.translate('counseling', 'info')} </x-text>
                  </tr-card>
                `,
                { forbiddenTemplate: nothing, context: ANY_CONTEXT },
              )
            : nothing}
          ${permissionGuard(
            Permission.VIEW_ADMIN_INSTITUTION_TIMEFRAMES,
            () => html`
              <tr-card href="/admin/institution-details/${institutionID}/timeframes">
                <x-h2> ${clientTranslator.translate('timeframe', 'plural')} </x-h2>
                <x-text> ${clientTranslator.translate('timeframe', 'info')} </x-text>
              </tr-card>
            `,
            { forbiddenTemplate: nothing, context: ANY_CONTEXT },
          )}
          ${permissionGuard(
            Permission.VIEW_ADMIN_INSTITUTION_SETTINGS,
            () => html`
              <tr-card href="/admin/institution-details/${institutionID}/settings">
                <x-h2> ${clientTranslator.translate('settings', 'name')} </x-h2>
                <x-text> ${clientTranslator.translate('settings', 'info')} </x-text>
              </tr-card>
            `,
            { forbiddenTemplate: nothing, context: ANY_CONTEXT },
          )}
          ${permissionGuard(
            Permission.VIEW_ADMIN_INSTITUTION_SETTINGS,
            () => html`
              <tr-card href="/admin/institution-details/${institutionID}/statistics">
                <x-h2> ${clientTranslator.translate('statistics', 'name')} </x-h2>
                <x-text> ${clientTranslator.translate('statistics', 'info')} </x-text>
              </tr-card>
            `,
            { forbiddenTemplate: nothing, context: ANY_CONTEXT },
          )}
          ${permissionGuard(
            Permission.VIEW_ADMIN_INSTITUTION_CATEGORIES,
            () => html`
              <tr-card href="/admin/institution-details/${institutionID}/categories">
                <x-h2> ${clientTranslator.translate('categories', 'plural')} </x-h2>
                <x-text> ${clientTranslator.translate('categories', 'info')} </x-text>
              </tr-card>
            `,
            { forbiddenTemplate: nothing, context: ANY_CONTEXT },
          )}
        </x-grid>
      </tr-flex>
    `;
  },
);

export const adminEinrichtungRouter = makeRouter(
  {
    root: {
      path: '/',
      render: params => {
        console.log('page-admin-einrichtung', params);
        return Overview.render({ institutionID: params.institutionID });
      },
    },
    home: {
      path: '/home',
      render: params => {
        console.log('home', params);
        return Overview.render({ institutionID: params.institutionID });
      },
    },
    workshops: {
      path: '/workshops*',
      render: params => {
        return html`
          <bh-page-admin-einrichtung-workshops
            institution-id=${params.institutionID}
          ></bh-page-admin-einrichtung-workshops>
        `;
      },
      subRouter: adminEinrichtungWorkshopsRouter,
    },
    'workshop-sets': {
      path: '/sets-workshop*',
      subRouter: adminEinrichtungWorkshopSetRouter,
    },
    [RouterInstitutionPages.COUNSELINGS]: {
      path: `/${RouterInstitutionPages.COUNSELINGS}*`,
      render: params => html`
        <bh-page-admin-einrichtung-counselings
          institution-id=${params.institutionID}
        ></bh-page-admin-einrichtung-counselings>
      `,
      subRouter: adminEinrichtungCounselingsRouter,
    },
    [RouterInstitutionPages.TIMEFRAMES]: {
      path: `/${RouterInstitutionPages.TIMEFRAMES}`,
      render: params => html`
        <bh-page-admin-einrichtung-timeframes
          institution-id=${params.institutionID}
        ></bh-page-admin-einrichtung-timeframes>
      `,
    },
    [RouterInstitutionPages.CATEGORIES]: {
      path: `/${RouterInstitutionPages.CATEGORIES}`,
      render: params => html`
        <bh-page-admin-einrichtung-categories
          institution-id=${params.institutionID}
        ></bh-page-admin-einrichtung-categories>
      `,
    },
    bundles: {
      path: '/bundles*',
      render: params => html`
        <bh-page-admin-einrichtung-bundles institution-id=${params.institutionID}></bh-page-admin-einrichtung-bundles>
      `,

      subRouter: adminEinrichtungBundlesRouter,
    },
    // TODO: add lecturers subrouter
    lecturers: {
      path: '/lecturers',
      render: params => html`
        <bh-page-admin-einrichtung-lecturers
          institution-id=${params.institutionID}
        ></bh-page-admin-einrichtung-lecturers>
      `,
    },
    user: {
      path: '/users*',
      subRouter: adminEinrichtungUserRouter,
    },
    lecturer: {
      path: '/lecturer/:lecturerID',
      render: params => html`
        <bh-page-admin-einrichtung-lecturer-detail
          institution-id=${params.institutionID}
          lecturer-id=${params.lecturerID ?? ''}
        ></bh-page-admin-einrichtung-lecturer-detail>
      `,
    },
    settings: {
      path: '/settings{/*}?',
      render: params => html`
        <bh-page-admin-einrichtung-settings institution-id=${params.institutionID}></bh-page-admin-einrichtung-settings>
      `,
      subRouter: adminEinrichtungSettingsRouter,
    },
    statistics: {
      path: '/statistics',
      metaData: {
        semesterSelect: true,
        breadcrumbs: ({ translate, params }) => [{ content: translate('statistics', 'name_inst'), active: true }],
      },
      render: params => {
        const content = html`
          <bh-page-admin-einrichtung-statistics
            institution-id=${params.institutionID}
          ></bh-page-admin-einrichtung-statistics>
        `;
        const options = { context: params.institutionID };
        return permissionGuard(Permission.VIEW_ADMIN_INSTITUTION, () => content, options);
      },
    },
    'import-export': {
      path: '/exports',
      metaData: {
        semesterSelect: true,
        breadcrumbs: ({ translate, params }) => [{ content: translate('imp_exp', 'name'), active: true }],
      },
      render: params => html`
        <bh-page-admin-einrichtung-import-export
          institution-id=${params.institutionID}
        ></bh-page-admin-einrichtung-import-export>
      `,
    },
    'form-config': {
      path: '/form-config',
      render: params => html`
        <bh-page-admin-einrichtung-form-config
          institution-id=${params.institutionID}
        ></bh-page-admin-einrichtung-form-config>
      `,
    },
  },
  {
    routerName: 'admin-einrichtungs-router',
    fallback: () => html` <x-empty-placeholder .img=${'close'} .text=${'404'} no-wrapper></x-empty-placeholder> `,
  },
);

/**
 * @element bh-page-admin-einrichtung
 */
@customElement('bh-page-admin-einrichtung')
export class BHPageAdminEinrichtung extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() private readonly _institution = new RXController(this, this.institutionID.observable, id =>
    id.pipe(switchMap(id => (!id ? of(null) : Institution.getByID<Institution>(id)(Institution.allFields)))),
  );

  @state() private readonly viewSettings = new RXController(
    this,
    combineLatest([this._institution.observable, currentUser()]).pipe(
      filter(([institution, user]) => !!institution?._id && !!user?._id),
      switchMap(([institution, user]) =>
        subscribeAdminInstitutionViewSetting(
          user!._id,
          institution!._id,
        )({
          selectedTimeframeID: 1,
          // FIXME: use functional components in the pages the filter are used in so it is not necessary to load them here
          institutionDetailsUsersFilterV1: selectionSet(() => BHFilter, 4),
          institutionDetailsLecturersFilterV1: selectionSet(() => BHFilter, 4),
          institutionDetailsExportParticipationsFilterV1: selectionSet(() => BHFilter, 4),
          institutionDetailsExportUsersFilterV1: selectionSet(() => BHFilter, 4),
          institutionDetailsExportProductsFilterV1: selectionSet(() => BHFilter, 4),
        }),
      ),
    ),
  );

  override render() {
    return html`
      <x-flex space="md" ${provideContextEntity(Institution, { $id: this._institution.value?._id })}>
        ${this._institution.value?.maintenanceMode
          ? html`
              <x-flex
                center
                crossaxis-center
                align="center"
                padding="md"
                ${css({ background: this.colors.tone.warning, color: this.colors.on.tone.warning })}
              >
                <x-text> ${this._translation.translate('admin_institution', 'maintenance_mode_active')} </x-text>
              </x-flex>
            `
          : nothing}
        ${watch(
          computed(() => {
            const content = adminEinrichtungRouter.outlet.value;
            return this.renderPage({ institutionID: this._institution.value?._id ?? '' }, content);
          }),
        )}
      </x-flex>
    `;
  }

  renderPage(params, content) {
    let currentRoute = adminEinrichtungRouter.currentRoute.value.route;
    let metaData = {} as any;
    while (currentRoute) {
      Object.assign(metaData, currentRoute.metaData ?? {});
      try {
        // FIXME: this should propably not throw errors
        currentRoute = currentRoute.subRouter?.currentRoute?.value?.route;
      } catch (e) {
        break;
      }
    }

    const actionConfigs = institutionPageActionConfig.value;
    console.log({ actionConfigs });
    const actions =
      actionConfigs.length > 0
        ? actionConfigs.map(config => {
            const primary = this.colors.accent.shade(400);
            const color = (() => {
              if (!config.color) return primary;
              if (config.color === 'primary') return primary;
              else return this.colors.tone.error;
            })();

            return html`<tr-button-small icon=${config.icon} .action=${() => config.callback()} .color=${color}>
              ${config.text ? html` <tr-text bold> ${config.text} </tr-text> ` : nothing}
            </tr-button-small>`;
          })
        : null;

    const breadcrumbs = metaData?.breadcrumbs?.({ params, translate: this._translation.translate });

    let semesterSelect = null as any;
    if (!!metaData?.semesterSelect) {
      semesterSelect = html`
        <bh-timeframe-selection institution-id=${params.institutionID ?? 0}></bh-timeframe-selection>
      `;
    }

    return html` <x-flex vertical space="xl">
      <x-flex horizontal>
        ${breadcrumbs
          ? html`
              <bh-breadcrumb
                .items=${breadcrumbs}
                .additionalAction=${html`<x-flex horizontal flex-end space="xs">
                  ${semesterSelect} ${actions}
                </x-flex>`}
              ></bh-breadcrumb>
            `
          : nothing}
      </x-flex>

      <section ${provideContextEntity(AdminInstitutionViewSetting, { $instance: this.viewSettings.value })}>
        ${content}
      </section>
    </x-flex>`;
  }
}
